import React from 'react';

import {
  AccountBalance as AccountBalanceIcon,
  ManageAccounts as ManageAccountsIcon,
  MenuBook as MenuBookFilledIcon,
  MonetizationOn as MonetizationOnIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import { useLocation } from 'react-router';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import type { MenuItem } from 'src/components/common';
import { ItemDetailsLayout, RedirectPage } from 'src/components/common';
import { VerifierBasicInfo } from 'src/components/verifier';
import { useVerifierById, useVerifierLogo, useVerifierParamId } from 'src/hooks/verifier';
import { buildUrl, getIsPathMatchLocation } from 'src/utils/router';

export const ClearVerifyVerifiersDetailLayout: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const verifierId = useVerifierParamId();
  const { verifier, loading } = useVerifierById(verifierId);
  const { verifierLogo, verifierLogoLoading, updateVerifierLogo, verifierLogoUpdating } =
    useVerifierLogo(verifier?.id);
  const location = useLocation();

  const getIsCurrentPath = React.useCallback(
    (path: string) => {
      return getIsPathMatchLocation(location, path);
    },
    [location],
  );

  const builtRoutes = React.useMemo(
    () => ({
      companyInformation: buildUrl(appRoutes.verifiers.detail.companyInformation, {
        [RouterEntitiesId.verifier]: verifierId,
      }),
      billingSettings: buildUrl(appRoutes.verifiers.detail.billingSettings, {
        [RouterEntitiesId.verifier]: verifierId,
      }),
      staff: buildUrl(appRoutes.verifiers.detail.staff, {
        [RouterEntitiesId.verifier]: verifierId,
      }),
      verificationPurposes: buildUrl(appRoutes.verifiers.detail.verificationPurposes, {
        [RouterEntitiesId.verifier]: verifierId,
      }),
    }),
    [verifierId],
  );

  const hasAccessToBilling = verifier?.type === 'Commercial';

  const menuItems: Array<MenuItem> = React.useMemo(
    () => [
      {
        link: builtRoutes.companyInformation,
        title: 'Company Information',
        icon: AccountBalanceIcon,
      },
      {
        link: builtRoutes.staff,
        title: 'Staff',
        icon: ManageAccountsIcon,
      },
      {
        link: builtRoutes.verificationPurposes,
        title: 'Verification Purposes',
        icon: MenuBookFilledIcon,
      },
    ],
    [builtRoutes.companyInformation, builtRoutes.staff, builtRoutes.verificationPurposes],
  );

  const hasAccessToBillingMenuItems: Array<MenuItem> = React.useMemo(
    () => [
      {
        link: builtRoutes.companyInformation,
        title: 'Company Information',
        icon: AccountBalanceIcon,
      },
      {
        link: builtRoutes.staff,
        title: 'Staff',
        icon: ManageAccountsIcon,
      },
      {
        link: builtRoutes.billingSettings,
        title: 'Billing Settings',
        icon: MonetizationOnIcon,
      },
      {
        link: builtRoutes.verificationPurposes,
        title: 'Verification Purposes',
        icon: MenuBookFilledIcon,
      },
    ],
    [
      builtRoutes.billingSettings,
      builtRoutes.companyInformation,
      builtRoutes.staff,
      builtRoutes.verificationPurposes,
    ],
  );

  if (
    getIsCurrentPath(appRoutes.verifiers.detail.billingSettings) &&
    !loading &&
    !hasAccessToBilling
  ) {
    return <RedirectPage />;
  }

  return (
    <ItemDetailsLayout
      name={verifier?.displayName}
      subtitleComponent={<VerifierBasicInfo verifierId={verifierId} />}
      logoUrl={verifierLogo?.downloadUrl || DEFAULT_AVATAR}
      menuItems={hasAccessToBilling ? hasAccessToBillingMenuItems : menuItems}
      logoProps={{
        onChange: updateVerifierLogo,
        loading: verifierLogoLoading || verifierLogoUpdating,
      }}
    >
      <Box>{children}</Box>
    </ItemDetailsLayout>
  );
};
